import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mail, MessageSquare, Share2, Ticket, User, Gift } from "lucide-react";
import { saveEmail, savePhoneNumber, saveGiveawayEntry, n8nSaveEmail, n8nSavePhone, n8nSaveEmailSS, n8nSavePhoneSS, n8nSavePhoneSlickText, n8nSaveGiveaway, sendMetaLeadEvent, recordPopupStat } from "@/lib/firebase";
import { validateEmail, validatePhone } from "@/lib/validation";
import { canSubmit, recordSubmission, getSecondsRemaining } from "@/lib/rate-limit";
import logoWhite from "@/assets/logo-white.svg";
import boatsGraphic from "@/assets/yrd-boats-graphic.svg";

// ─────────────────────────────────────────────────────────────────────────
// GIVEAWAY CAMPAIGN TOGGLE
// `true`  → 1-in-50 "win 2 free GA" pop-up: phone → name+email → entered,
//           fires on scroll-30% / 25s / desktop exit-intent, once per visitor.
//           Phone submit hits the /yrd-giveaway webhook (server-side win roll).
// `false` → normal email-capture pop-up (3s timer, standard welcome text).
const GIVEAWAY_ENABLED = false;

// FINAL-DAY SELL (2026-07-18): Day One is done — the auto-pop-up stops collecting
// emails and sells Saturday tickets instead. Button-opened ("Get Updates")
// instances still show the signup form. Flip to false after the festival.
const SATURDAY_SELL = true;

const TICKETS_URL = "/tickets";

// Themed tokens (on-brand: ocean blue card, golden-yellow CTA).
const CARD_BG = "bg-yacht-blue";
const BTN = "bg-yacht-yellow text-yacht-charcoal";
const RING = "focus:ring-yacht-yellow";

// Fire a pop-up funnel event to BOTH GA4 (gtag) and Meta Pixel (fbq) so we can
// measure opens / closes / conversion. GA4 sees a snake_case custom event;
// Meta sees a trackCustom event of the same name (separate from the standard
// "Lead" conversion events fired on submit).
const trackPopup = (name: string, params: Record<string, unknown> = {}) => {
  if (typeof window === "undefined") return;
  const w = window as unknown as {
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
  };
  const data = { campaign: GIVEAWAY_ENABLED ? "giveaway-1in50" : SATURDAY_SELL ? "saturday-sell" : "email-capture", ...params };
  try { w.gtag?.("event", name, data); } catch { /* noop */ }
  try { w.fbq?.("trackCustom", name, data); } catch { /* noop */ }
};

// Auto-open delay — shortened from 25s ("wrong timing" fix). The pop-up still
// opens on the EARLIEST of this, scroll-30%, or exit-intent.
const AUTO_OPEN_MS = 7000;

// 50/50 A/B test: "interrupt" shows a 3-2-1 countdown teaser before the pop-up
// (a pattern interrupt to break scroll autopilot); "control" opens directly.
// Stats are recorded per-variant so the dashboard can prove which converts better.
const PATTERN_INTERRUPT_AB = true;

const pickVariant = (): "control" | "interrupt" => {
  try {
    let v = localStorage.getItem("yrd_popup_variant");
    if (v !== "control" && v !== "interrupt") {
      v = Math.random() < 0.5 ? "interrupt" : "control";
      localStorage.setItem("yrd_popup_variant", v);
    }
    return v as "control" | "interrupt";
  } catch { return "control"; }
};

interface EarlyAccessModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  initialStep?: "email" | "phone";
}

const EarlyAccessModal = ({ isOpen: externalIsOpen, onClose: externalOnClose, initialStep }: EarlyAccessModalProps) => {
  const firstStep: "email" | "phone" = initialStep ?? (GIVEAWAY_ENABLED ? "phone" : "email");
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [step, setStep] = useState<"email" | "phone" | "done">(firstStep);
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const variantRef = useRef<"control" | "interrupt">("control");
  const openedRef = useRef(false);
  const viewTrackedRef = useRef(false);
  // How this instance was opened: "auto" = the homepage scroll/25s/exit-intent
  // pop-up (the real conversion-funnel denominator); "manual" = a Get-Updates
  // button. Controlled instances are always button-driven, so default "manual".
  const openTriggerRef = useRef<"auto" | "manual">("manual");

  const isControlled = externalIsOpen !== undefined;
  const isOpen = isControlled ? externalIsOpen : internalIsOpen;
  const setIsOpen = isControlled ? (val: boolean) => { if (!val && externalOnClose) externalOnClose(); } : setInternalIsOpen;

  // True only for the genuine homepage auto-pop-up (not button-opened or
  // controlled instances) — keeps the dashboard funnel internally consistent.
  const isAutoPopup = () => !isControlled && openTriggerRef.current === "auto";
  // Each counter is recorded twice: a total and a per-variant key (e.g. opens +
  // opens_interrupt) so the dashboard can compare the A/B test.
  const statKeys = (base: string) => [base, `${base}_${variantRef.current}`];

  // Count each pop-up impression exactly once per mount (the denominator for
  // conversion rate: opens → entries). Includes which trigger surfaced it.
  useEffect(() => {
    if (isOpen && !viewTrackedRef.current) {
      viewTrackedRef.current = true;
      trackPopup("popup_view", { controlled: isControlled, trigger: openTriggerRef.current, variant: variantRef.current });
      if (isAutoPopup()) recordPopupStat(statKeys("opens"));
    }
  }, [isOpen, isControlled]);

  // Lock background scroll while the pop-up is open (mobile fix — stops the page
  // scrolling behind the modal and the janky double-scroll feel).
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  // Auto-show trigger (uncontrolled only)
  useEffect(() => {
    if (isControlled) return;

    // Saturday sell uses its own dismissal key — visitors who dismissed the old
    // email pop-up still get exactly one look at the final-day ticket push.
    if (SATURDAY_SELL) {
      if (localStorage.getItem("yrd_sat_sell_dismissed") === "true") return;
    } else {
      const dismissed = localStorage.getItem("yrd_giveaway_dismissed") === "true";
      const completed = localStorage.getItem("yrd_giveaway_completed") === "true";
      if (dismissed || completed) return;
    }

    let cdInterval: ReturnType<typeof setInterval> | null = null;

    const open = () => {
      if (openedRef.current) return;
      openedRef.current = true;
      openTriggerRef.current = "auto";
      variantRef.current = pickVariant();

      if (GIVEAWAY_ENABLED && PATTERN_INTERRUPT_AB && variantRef.current === "interrupt") {
        // Pattern interrupt: flash a 3-2-1 countdown teaser, then open.
        let n = 3;
        setCountdown(n);
        cdInterval = setInterval(() => {
          n -= 1;
          if (n <= 0) {
            if (cdInterval) clearInterval(cdInterval);
            setCountdown(null);
            setInternalIsOpen(true);
          } else {
            setCountdown(n);
          }
        }, 1000);
      } else {
        setInternalIsOpen(true);
      }
    };

    if (!GIVEAWAY_ENABLED) {
      // Normal mode: simple 3s delay.
      const t = setTimeout(open, 3000);
      return () => clearTimeout(t);
    }

    // Giveaway mode: open on the EARLIEST of scroll-30% / AUTO_OPEN_MS / desktop exit-intent.
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const pct = max > 0 ? window.scrollY / max : 0;
      if (window.scrollY > 600 || pct > 0.3) open();
    };
    const onExit = (e: MouseEvent) => {
      if (e.clientY <= 0) open();
    };
    const timer = setTimeout(open, AUTO_OPEN_MS);
    window.addEventListener("scroll", onScroll, { passive: true });
    const isDesktop = typeof window.matchMedia === "function" && window.matchMedia("(pointer: fine)").matches;
    if (isDesktop) document.addEventListener("mouseout", onExit);

    return () => {
      clearTimeout(timer);
      if (cdInterval) clearInterval(cdInterval);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("mouseout", onExit);
    };
  }, [isControlled]);

  // Manual trigger from buttons (uncontrolled only)
  useEffect(() => {
    if (isControlled) return;
    const handleOpenModal = () => { openTriggerRef.current = "manual"; setInternalIsOpen(true); };
    window.addEventListener("openEarlyAccess", handleOpenModal);
    return () => window.removeEventListener("openEarlyAccess", handleOpenModal);
  }, [isControlled]);

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone) return;
    if (!canSubmit("phone")) {
      setErrorMessage(`Please wait ${getSecondsRemaining("phone")}s before submitting again.`);
      return;
    }
    const validationError = validatePhone(phone);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      await Promise.all([
        savePhoneNumber(phone, "early-access"),
        n8nSavePhone(phone, "early-access").catch(console.error),
        n8nSavePhoneSS(phone, "early-access").catch(console.error),
        // Giveaway: server-side 1-in-50 roll + win/entered SMS. Replaces the
        // generic welcome text so each entrant gets exactly one message.
        (GIVEAWAY_ENABLED
          ? n8nSaveGiveaway(phone)
          : n8nSavePhoneSlickText(phone)
        ).catch(console.error),
        sendMetaLeadEvent({ phone }, "early-access").catch(console.error),
      ]);
      localStorage.setItem("yrd_phone_submitted", "true");
      recordSubmission("phone");
      if (typeof window !== "undefined" && (window as any).fbq) {
        (window as any).fbq("track", "Lead", { content_name: "giveaway-phone" });
      }
      trackPopup("popup_entry_phone");
      if (isAutoPopup()) recordPopupStat(statKeys("entriesPhone"));

      if (GIVEAWAY_ENABLED) {
        // Phone is the entry; go collect name + email for fulfillment.
        setSuccessMessage("Result incoming — check your texts! 📲");
        setTimeout(() => {
          setStep("email");
          setSuccessMessage(null);
        }, 800);
      } else {
        localStorage.setItem("yrd_giveaway_completed", "true");
        setSuccessMessage("You're on the list!");
        setStep("done");
      }
    } catch (error) {
      console.error("Error saving phone:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    if (GIVEAWAY_ENABLED && !firstName.trim()) {
      setErrorMessage("Please add your first name.");
      return;
    }
    if (!canSubmit("email")) {
      setErrorMessage(`Please wait ${getSecondsRemaining("email")}s before submitting again.`);
      return;
    }
    const validationError = validateEmail(email);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      const tasks: Promise<unknown>[] = [
        saveEmail(email, "early-access"),
        n8nSaveEmail(email, "early-access").catch(console.error),
        n8nSaveEmailSS(email, "early-access").catch(console.error),
        sendMetaLeadEvent({ email }, "early-access").catch(console.error),
      ];
      if (GIVEAWAY_ENABLED) {
        tasks.push(saveGiveawayEntry({ phone, firstName, email }).catch(console.error));
      }
      await Promise.all(tasks);
      localStorage.setItem("yrd_email_submitted", "true");
      recordSubmission("email");
      if (typeof window !== "undefined" && (window as any).fbq) {
        (window as any).fbq("track", "Lead", { content_name: "giveaway-email" });
      }
      trackPopup("popup_entry_email");
      if (isAutoPopup()) recordPopupStat(statKeys("entriesEmail"));

      if (GIVEAWAY_ENABLED) {
        localStorage.setItem("yrd_giveaway_completed", "true");
        setSuccessMessage(null);
        setStep("done");
      } else {
        setSuccessMessage("Email saved!");
        setTimeout(() => {
          setStep("phone");
          setSuccessMessage(null);
        }, 800);
      }
    } catch (error) {
      console.error("Error saving email:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Closed via X / backdrop. `step` tells us where they bailed (phone vs
    // email vs after done) so we can see the drop-off point.
    trackPopup("popup_dismiss", { step });
    if (isAutoPopup()) recordPopupStat(statKeys("closes"));
    if (!isControlled) {
      localStorage.setItem(SATURDAY_SELL ? "yrd_sat_sell_dismissed" : "yrd_giveaway_dismissed", "true");
    }
    if (externalOnClose) externalOnClose();
    else setInternalIsOpen(false);
  };

  return (
    <>
    {/* Pattern interrupt: 3-2-1 countdown teaser (A/B "interrupt" variant only) */}
    <AnimatePresence>
      {countdown !== null && (
        <motion.div
          key="cd"
          className="fixed top-5 left-1/2 -translate-x-1/2 z-[120] flex items-center gap-2 rounded-full bg-yacht-yellow px-5 py-3 text-sm sm:text-base font-bold text-yacht-charcoal shadow-2xl"
          initial={{ opacity: 0, y: -24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -24 }}
        >
          <Gift className="w-4 h-4" />
          <span>Free GA ticket giveaway opening in</span>
          <motion.span key={countdown} initial={{ scale: 1.6 }} animate={{ scale: 1 }} className="inline-block min-w-[1ch] text-lg">{countdown}</motion.span>
        </motion.div>
      )}
    </AnimatePresence>

    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />

          <motion.div
            className="fixed inset-0 z-[101] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className={`relative w-full max-w-md ${CARD_BG} rounded-3xl overflow-hidden shadow-2xl`}
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={boatsGraphic}
                alt=""
                className="absolute -bottom-20 -right-20 w-[300px] opacity-10 pointer-events-none"
                style={{ filter: "brightness(0) invert(1)" }}
              />

              <button
                onClick={handleClose}
                className="absolute top-3 right-3 p-3 text-white/40 hover:text-white/70 transition-colors z-20"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="relative z-10 p-6 pt-9 sm:p-8 sm:pt-10 max-h-[88vh] overflow-y-auto">
                <div className="flex justify-center mb-5 sm:mb-6">
                  <img src={logoWhite} alt="Yacht Rock Detroit" className="h-12 sm:h-16" />
                </div>

                {SATURDAY_SELL && !isControlled ? (
                  <motion.div className="text-center" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                    <div className="mb-3 inline-flex items-center gap-1 rounded-full bg-yacht-yellow px-3 py-1 text-xs font-bold uppercase tracking-wider text-yacht-charcoal">
                      <Ticket className="w-3.5 h-3.5" /> This Is It — One More Day
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                      Day One Was <span className="text-yacht-yellow">Insane.</span>
                    </h2>
                    <p className="text-white/80 mb-6">
                      We're back — bigger and better — <span className="font-bold text-white">Saturday, doors at noon</span>.
                      Three Dog Night, Ambrosia, and the biggest bill of the weekend.
                    </p>
                    <div className="flex flex-col gap-3">
                      <a
                        href={TICKETS_URL}
                        onClick={() => trackPopup("popup_ticket_click", { campaign: "saturday-sell" })}
                        className={`inline-flex items-center justify-center gap-2 px-8 py-4 ${BTN} font-bold text-lg rounded-full hover:scale-105 transition-transform`}
                      >
                        <Ticket className="w-5 h-5" />
                        Get Saturday Tickets →
                      </a>
                      <p className="text-white/60 text-sm">
                        Don't miss it — the next one is 365 days away.
                      </p>
                      <button onClick={handleClose} className="text-white/50 hover:text-white text-sm underline transition-colors">
                        Maybe later
                      </button>
                    </div>
                  </motion.div>
                ) : step === "done" ? (
                  <motion.div className="text-center" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                    {GIVEAWAY_ENABLED ? (
                      <>
                        <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Check your texts! 📲</h2>
                        <p className="text-white/70 mb-6">
                          Win or lose, <span className="font-bold text-white">your result just hit your phone</span>.
                          Didn't win? Don't leave the river to chance:
                        </p>
                        <div className="flex flex-col gap-3">
                          <a
                            href={TICKETS_URL}
                            className={`inline-flex items-center justify-center gap-2 px-8 py-4 ${BTN} font-bold text-lg rounded-full hover:scale-105 transition-transform`}
                          >
                            <Ticket className="w-5 h-5" />
                            Grab Tickets Now
                          </a>
                          <button onClick={() => setIsOpen(false)} className="text-white/50 hover:text-white text-sm underline transition-colors">
                            Maybe later
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">You're on the list! 🎉</h2>
                        <p className="text-white/70 mb-6">Tickets are on sale now — get yours before they sell out!</p>
                        <div className="flex flex-col gap-3">
                          <a href={TICKETS_URL} className={`inline-flex items-center justify-center gap-2 px-8 py-3 ${BTN} font-bold rounded-full hover:scale-105 transition-transform`}>
                            <Ticket className="w-4 h-4" />
                            Get Tickets
                          </a>
                          <button onClick={() => setIsOpen(false)} className="text-white/50 hover:text-white text-sm underline transition-colors">
                            Skip for now
                          </button>
                        </div>
                      </>
                    )}
                  </motion.div>
                ) : (
                  <>
                    <div className="text-center mb-6">
                      {GIVEAWAY_ENABLED ? (
                        <>
                          <div className="mb-2 inline-flex items-center gap-1 rounded-full bg-yacht-yellow px-3 py-1 text-xs font-bold uppercase tracking-wider text-yacht-charcoal">
                            <Gift className="w-3.5 h-3.5" /> Instant-Win Giveaway
                          </div>
                          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                            {step === "phone" ? <>Win <span className="text-yacht-yellow">2 FREE</span> GA Tickets</> : "Check your texts! 📲"}
                          </h2>
                          <p className="text-white/70">
                            {step === "phone"
                              ? <><span className="font-bold text-white">1 in 50 wins</span> a pair — and you find out the <span className="font-bold text-white">second you enter</span>. Drop your number, get your result by text instantly.</>
                              : "We just texted your result 📲 Add your name + email so we know where to send your tickets if you won."}
                          </p>
                        </>
                      ) : (
                        <>
                          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">Join the Community</h2>
                          <p className="text-white/70">
                            {step === "email"
                              ? <>Stay in the know — be a part of the Yacht Rock Detroit community.</>
                              : "One more step — get text alerts so you don't miss it."}
                          </p>
                        </>
                      )}
                    </div>

                    {step === "phone" ? (
                      <motion.form onSubmit={handlePhoneSubmit} className="space-y-4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} key="phone-form">
                        <label htmlFor="ga-phone" className="block text-sm font-semibold text-white mb-1">Enter your phone number</label>
                        <div className="relative">
                          <MessageSquare className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-yacht-charcoal/40" />
                          <input
                            type="tel"
                            id="ga-phone"
                            name="phone"
                            autoComplete="tel"
                            inputMode="tel"
                            placeholder="(555) 123-4567"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            required
                            className={`w-full pl-12 pr-4 py-4 bg-white rounded-xl text-yacht-charcoal placeholder:text-yacht-charcoal/40 focus:outline-none focus:ring-2 ${RING} text-lg`}
                          />
                        </div>
                        <button type="submit" disabled={isSubmitting} className={`w-full py-4 ${BTN} font-bold text-lg rounded-full hover:scale-[1.02] transition-transform disabled:opacity-50`}>
                          {isSubmitting ? "Rolling the dice..." : GIVEAWAY_ENABLED ? "Enter & Find Out Instantly →" : "Complete Signup"}
                        </button>
                        {errorMessage && <p className="text-red-300 text-center text-sm font-medium">{errorMessage}</p>}
                        {successMessage && <p className="text-green-300 text-center font-medium">{successMessage}</p>}
                        {GIVEAWAY_ENABLED && (
                          <p className="text-center text-xs text-white/40">No purchase necessary. We'll only text about your entry.</p>
                        )}
                      </motion.form>
                    ) : (
                      <motion.form onSubmit={handleEmailSubmit} className="space-y-4" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} key="email-form">
                        {GIVEAWAY_ENABLED && (
                          <div>
                            <label htmlFor="ga-name" className="block text-sm font-semibold text-white mb-1">First name</label>
                            <div className="relative">
                              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-yacht-charcoal/40" />
                              <input
                                type="text"
                                id="ga-name"
                                name="firstName"
                                autoComplete="given-name"
                                placeholder="First name"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                required
                                className={`w-full pl-12 pr-4 py-4 bg-white rounded-xl text-yacht-charcoal placeholder:text-yacht-charcoal/40 focus:outline-none focus:ring-2 ${RING} text-lg`}
                              />
                            </div>
                          </div>
                        )}
                        <div>
                          <label htmlFor="ga-email" className="block text-sm font-semibold text-white mb-1">Enter your email</label>
                          <div className="relative">
                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-yacht-charcoal/40" />
                            <input
                              type="email"
                              id="ga-email"
                              name="email"
                              autoComplete="email"
                              inputMode="email"
                              placeholder="your@email.com"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              required
                              className={`w-full pl-12 pr-4 py-4 bg-white rounded-xl text-yacht-charcoal placeholder:text-yacht-charcoal/40 focus:outline-none focus:ring-2 ${RING} text-lg`}
                            />
                          </div>
                        </div>
                        <button type="submit" disabled={isSubmitting} className={`w-full py-4 ${BTN} font-bold text-lg rounded-full hover:scale-[1.02] transition-transform disabled:opacity-50`}>
                          {isSubmitting ? "Saving..." : GIVEAWAY_ENABLED ? "Lock In My Entry →" : "Stay Updated"}
                        </button>
                        {errorMessage && <p className="text-red-300 text-center text-sm font-medium">{errorMessage}</p>}
                        {successMessage && <p className="text-green-300 text-center font-medium">{successMessage}</p>}
                      </motion.form>
                    )}
                  </>
                )}
              </div>

              <div className="h-2 bg-yacht-yellow" />
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
    </>
  );
};

export default EarlyAccessModal;
